import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Feather, Film, Sparkles, ShoppingBag } from "lucide-react";
import { goToGoogle } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

const CARD = "#24113d";
const PERKS = [
  [Feather, "Share thoughts"], [Film, "Post reels"], [Sparkles, "Make your avatar"], [ShoppingBag, "Buy bracelets"],
];

const SceneSide = () => (
  <div className="relative h-56 md:h-auto md:min-h-[520px] overflow-hidden" data-testid="signin-scene">
    <img src="/img/signin-deer.jpg" alt="Two deer by a lake under a crescent moon" className="absolute inset-0 w-full h-full object-cover object-bottom" />
    <p className="absolute left-0 right-0 md:right-[18%] top-[22%] md:top-[30%] text-center font-display text-2xl md:text-4xl tracking-[0.45em] text-white/90 [text-shadow:0_2px_12px_rgba(36,17,61,0.6)]">
      WELCOME
    </p>
    <svg viewBox="0 0 100 400" preserveAspectRatio="none" aria-hidden="true" className="hidden md:block absolute right-0 top-0 h-full w-[22%]">
      <path d="M100 0 H60 C10 60 95 120 50 200 C10 270 80 330 30 400 H100 Z" fill={CARD} />
    </svg>
    <svg viewBox="0 0 400 60" preserveAspectRatio="none" aria-hidden="true" className="md:hidden absolute bottom-0 left-0 w-full h-10">
      <path d="M0 60 V35 C80 0 140 60 220 30 C290 5 350 40 400 20 V60 Z" fill={CARD} />
    </svg>
  </div>
);

export const SkyBackdrop = () => (
  <>
    <motion.img src="/img/signin-sky.jpg" alt="" initial={{ scale: 1.15 }} animate={{ scale: 1 }}
      transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-[#0e0616]/45" />
  </>
);

export const SignInScreen = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("lz:signin", show);
    return () => window.removeEventListener("lz:signin", show);
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[70] grid place-items-center px-5 overflow-hidden" data-testid="signin-screen" data-lenis-prevent>
          <SkyBackdrop />
          <button onClick={() => setOpen(false)} aria-label="Close" data-testid="signin-close-btn"
            className="absolute z-10 top-3 right-3 md:top-5 md:right-5 p-3 rounded-full bg-[#0e0616]/60 backdrop-blur hover:bg-[#0e0616]/80 transition-colors">
            <X className="w-5 h-5" />
          </button>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-[32px] grid md:grid-cols-2 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.8)] border border-purple-200/20"
            style={{ background: CARD }} data-testid="signin-card">
            <SceneSide />
            <div className="px-7 pb-8 pt-4 md:p-12 flex flex-col justify-center">
              <LogoMark className="w-11 h-11" />
              <h2 className="mt-5 font-display text-3xl md:text-4xl leading-tight" data-testid="signin-title">
                Hello!<br /><span className="text-amber-200">We are glad to see you</span>
              </h2>
              <p className="mt-3 text-sm text-purple-100/90">Sign in to Lavender Hollow to join the community.</p>
              <button onClick={goToGoogle} data-testid="signin-google-btn"
                className="mt-7 w-full inline-flex items-center justify-center gap-3 rounded-full bg-purple-100 text-[#24113d] py-3.5 font-medium hover:bg-amber-200 transition-colors duration-500">
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true"><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.7z"/><path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24z"/><path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6h-4a12 12 0 0 0 0 10.8l4-3.1z"/><path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z"/></svg>
                Continue with Google
              </button>
              <div className="mt-7 flex items-center gap-3 text-xs text-purple-100/80">
                <span className="h-px flex-1 bg-purple-200/25" /> With your account you can <span className="h-px flex-1 bg-purple-200/25" />
              </div>
              <ul className="mt-4 grid grid-cols-2 gap-3" data-testid="signin-perks">
                {PERKS.map(([Icon, label]) => (
                  <li key={label} className="flex items-center gap-2 rounded-2xl border border-purple-200/20 px-3 py-2.5 text-sm text-purple-50">
                    <Icon className="w-4 h-4 text-amber-200 shrink-0" /> {label}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-purple-100/80">We only use your Google name and email to sign you in. We never see your password.</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
